import { Alert, AlertTitle, Box, Button, Link, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { useState } from 'react';

import { useGetDossierData } from '../../../../resources/dossier/queries';
import { DossierId } from '../../../../resources/dossier/types';
import { DEFAULT_VIEW_ROUTE } from '../../../../shared/routing/Routes';
import {
    useInvalidateDuplicateMutation,
    useValidateDuplicateMutation,
} from '../../../../resources/dossier/mutations';

import { DuplicateModal } from './DuplicateModal';

export const DuplicateAlert = () => {
    const { formatMessage } = useIntl();
    const history = useHistory();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { dossier } = useGetDossierData(dossierId) || {};
    const validateDuplicateMutation = useValidateDuplicateMutation(dossierId);
    const invalidateDuplicateMutation = useInvalidateDuplicateMutation(dossierId);
    const [openValidateModal, setOpenValidateModal] = useState<boolean>(false);
    const [openInvalidateModal, setOpenInvalidateModal] = useState<boolean>(false);

    if (!dossier) {
        return null;
    }
    const { duplicateState, duplicateFromDossierId } = dossier;

    const handleValidateDuplicateClick = () => {
        validateDuplicateMutation.mutate(undefined, {
            onSuccess: () => {
                const hasNavigatedInRequestManager = history.action !== 'POP';
                hasNavigatedInRequestManager ? history.goBack() : history.push(DEFAULT_VIEW_ROUTE);
            },
        });
    };
    const handleInvalidateDuplicateClick = () => {
        invalidateDuplicateMutation.mutate();
    };

    return (
        <>
            <Alert severity="error" sx={{ marginBottom: 2, maxWidth: '100%' }}>
                {duplicateState === 'found' && (
                    <>
                        <AlertTitle variant="body2" color="textError" fontWeight="bold">
                            {dossier.duplicateFromDossierId
                                ? formatMessage(
                                      {
                                          id: 'dossier.duplicate.alert.description_part_1_with_dossier_id',
                                      },
                                      {
                                          dossierIdLink: (
                                              <Link
                                                  href={`/request-manager/dossier/${duplicateFromDossierId}/info`}
                                                  target="_blank"
                                                  color="inherit"
                                                  sx={{ fontWeight: 'inherit' }}
                                              >
                                                  {`#${duplicateFromDossierId}`}
                                              </Link>
                                          ),
                                      },
                                  )
                                : formatMessage({
                                      id: 'dossier.duplicate.alert.description_part_1',
                                  })}
                        </AlertTitle>
                        <Typography variant="body2" color="textError">
                            {formatMessage({
                                id: 'dossier.duplicate.alert.description_part_2',
                            })}
                        </Typography>
                        <Box display="flex" gap={1.5} paddingTop={1.5} flexWrap="wrap">
                            <Button
                                color="error"
                                sx={{ fontWeight: 600 }}
                                onClick={() => setOpenValidateModal(true)}
                            >
                                {formatMessage({
                                    id: 'dossier.duplicate.alert.validate_button',
                                })}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setOpenInvalidateModal(true)}
                            >
                                {formatMessage({
                                    id: 'dossier.duplicate.alert.invalidate_button',
                                })}
                            </Button>
                        </Box>
                    </>
                )}
                {duplicateState === 'confirmed' && (
                    <Typography variant="body1">
                        {formatMessage({
                            id: 'dossier.duplicate.alert.description_duplicate_confirmed_part_1',
                        })}
                        <br />
                        {formatMessage({
                            id: 'dossier.duplicate.alert.description_duplicate_confirmed_part_2',
                        })}
                    </Typography>
                )}
            </Alert>
            <DuplicateModal
                open={openValidateModal}
                setOpen={setOpenValidateModal}
                title={formatMessage({
                    id: 'dossier.duplicate.validate_duplicate_dialog.title',
                })}
                description={formatMessage({
                    id: 'dossier.duplicate.validate_duplicate_dialog.description',
                })}
                confirmButtonTitle={formatMessage({
                    id: 'dossier.duplicate.validate_duplicate_dialog.confirm_button',
                })}
                confirmButtonColor={'error'}
                handleConfirmation={handleValidateDuplicateClick}
            />
            <DuplicateModal
                open={openInvalidateModal}
                setOpen={setOpenInvalidateModal}
                title={formatMessage({
                    id: 'dossier.duplicate.invalidate_duplicate_dialog.title',
                })}
                description={formatMessage({
                    id: 'dossier.duplicate.invalidate_duplicate_dialog.description',
                })}
                confirmButtonTitle={formatMessage({
                    id: 'dossier.duplicate.invalidate_duplicate_dialog.confirm_button',
                })}
                confirmButtonColor={'primary'}
                handleConfirmation={handleInvalidateDuplicateClick}
            />
        </>
    );
};
